/* Back Hero USA — free-gift cart guard (29/09/2026).
 *
 * Port of the bundle-standard nested-gift guard (shopify-bundle-flow references/nested-gifts.md + gift-rules.md;
 * planner = Titanium Wares tw-st-gift-guard-v3 "per (product, gift) pair", Horizon 4.x plumbing = SCENTREAT
 * st-pillar-gift-guard-v3: CartLinesUpdateEvent from @shopify/events, lower → add with parent_line_key → update {}).
 *
 * The $0 gift products ride as CHILD lines of the paid product that earned them (Shopify nested cart lines):
 *   - /cart/add.js with parent_id works only for a parent variant in the SAME request (the buy box does that);
 *     a line already in the cart takes children only by parent_line_key (parent_id → 422);
 *   - removing the parent removes its children server-side; children never follow the parent's quantity;
 *   - line keys change after most edits → the memory keeps the holder's VARIANT, never keys;
 *   - /cart.js carries parent_relationship.parent_key (Liquid cart | json does not) → always plan from /cart.js.
 *
 * Owner rules for every store (29/09/2026, gift-rules.md "Guard behaviour"):
 *   1 gifts = entitled(product, paid units of that product across all its variants); $0 gift lines never count;
 *   2 a gift's quantity raised above what its product earns → the gift stays at the earned qty and the EXCESS becomes a
 *     normal paid line of the real product, same variant/size;
 *   3 a gift the customer deletes stays deleted until the product is added again (12/10/2026: any new add of the
 *     product - its page, + in the cart, an upsell - brings back every gift it earns); the paid product is untouched;
 *   4 the real item added again (its own page, upsell, cart link) → up to the earned qty it turns into the free gift under
 *     its product, same size; the rest stays paid;
 *   5 paid qty lowered → gifts above the new entitlement are TRIMMED, never turned into paid lines;
 *   6 paid qty raised past the next tier → the newly earned gifts are added (12/10/2026: a raise refills deleted gifts too, see 3);
 *   7 above the top tier → entitled() below. Owner 30/09/2026: gifts scale by COMBO — the paid units split greedily into the
 *     product's tiers, biggest first, and each piece brings its tier's gift set (TF 3 = Buy 2 set + Buy 1 set).
 *
 * Rules come from script[data-bh-gift-rules] (snippets/bh-product-rules.liquid on product pages; a store-wide copy with
 * "complete": true is meant for the cart drawer). With incomplete rules the guard never touches gifts that hang under a
 * product it has no rules for. A $0 gift line with no parent is always removed (re-hung first when a product earns it).
 *
 * Also ported: the checkout gate (Check out waits until every cart request has settled and a fresh check ran, then posts
 * without the updates[] boxes) and the wallet-button lock while a check is due. Not ported: the fast +/− path of the drawer
 * (the cart phase decides). A cart link that skips the storefront is caught by the post-order Flow check only.
 * Exposes window.BH.giftGuard (rules, entitled, plan, check, idle, giftsForAdd, notePdpAdd, noteCart).
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const BH = (window.BH = window.BH || {});
if (!BH.giftGuard) setup();

function setup() {
  const KEY = 'bh-gift-guard:v1';
  const SOURCE = 'bh-gift-guard';
  const root = document.documentElement;
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
  const routes = () => window.Theme?.routes || { cart_url: '/cart', cart_add_url: '/cart/add', cart_update_url: '/cart/update' };
  const jsonUrl = (u) => (u.endsWith('.js') ? u : `${u}.js`);

  /* ---------------- rules ---------------- */
  let R = { complete: false, products: {}, gifts: {}, claims: {}, realOf: {} };
  function readRules() {
    const out = { complete: false, products: {}, gifts: {}, claims: {}, realOf: {} };
    for (const el of document.querySelectorAll('script[data-bh-gift-rules]')) {
      let j;
      try {
        j = JSON.parse(el.textContent);
      } catch {
        continue;
      }
      if (j.complete) out.complete = true;
      Object.assign(out.products, j.products || {});
      for (const [h, g] of Object.entries(j.gifts || {})) {
        const was = out.gifts[h] || {};
        out.gifts[h] = { ...was, ...g, real: { ...(was.real || {}), ...(g.real || {}) } };
      }
    }
    for (const [h, g] of Object.entries(out.gifts)) {
      for (const [gv, rv] of Object.entries(g.real || {})) {
        out.realOf[gv] = Number(rv); // gift variant -> real variant (#2)
        if (!out.claims[rv]) out.claims[rv] = { gift: h, variant: Number(gv) }; // real variant -> gift variant (#4)
      }
    }
    R = out;
    return out;
  }

  /**
   * What `units` paid units of `handle` earn: { gift handle: qty }.
   * Owner 30/09/2026 (Back Hero, replaces "the top tier's set"): "buy 3 thì thêm 2 free khác.. buy 4 thì add thêm 4 free khác".
   * Gifts scale by COMBO: split the units greedily into the product's tiers, biggest tier that fits first, again and again, and add up
   * each piece's gift set (tier sets are cumulative). TF: 3 = [2]+[1] → belt 2, mystery 2, patch 2; 5 = [2]+[2]+[1] → 3, 3, 4.
   * Foot sleeve (tiers 2 = no gift, 4 = patch + mystery): 7 = [4]+[2]+1 left → 1 + 1; 8 = [4]+[4] → 2 + 2. Units that fit no tier earn nothing.
   */
  function entitled(handle, units) {
    const tiers = [...(R.products[handle]?.tiers || [])].filter((t) => Number(t.units) > 0).sort((a, b) => b.units - a.units);
    const out = {};
    let left = Number(units) || 0;
    while (left > 0) {
      const t = tiers.find((x) => x.units <= left);
      if (!t) break;
      for (const [g, n] of Object.entries(t.gifts || {})) out[g] = (out[g] || 0) + n;
      left -= t.units;
    }
    return out;
  }

  /* ---------------- memory (per cart token, shared by the tabs of this browser) ---------------- */
  const tokenOf = (cart) => String(cart?.token || '').split('?')[0];
  const readMem = (token) => {
    try {
      const m = JSON.parse(localStorage.getItem(KEY) || 'null');
      if (m && m.token === token) return m;
    } catch {}
    return { token, pairs: {}, real: {}, fresh: true };
  };
  const writeMem = (m) => {
    try {
      localStorage.setItem(KEY, JSON.stringify({ token: m.token, pairs: m.pairs || {}, real: m.real || {} }));
    } catch {}
  };

  /* ---------------- planner (pure: the cart in, the writes out) ---------------- */
  /**
   * @returns {{updates:Object, adds:Array, paid:Array, next:Object, cart:Object}}
   *   updates: line key -> qty (lower gifts, drop loose gifts, lower real lines that turn into gifts)
   *   adds:    [{id, quantity, parentVariant, pair}] gifts to hang under a product line (parent_line_key)
   *   paid:    [{id, quantity}] real product units the customer asked for above the entitlement (#2)
   */
  function plan(cart, opt = {}) {
    readRules();
    const items = cart.items || [];
    const byKey = new Map(items.map((i) => [i.key, i]));
    const mem = readMem(tokenOf(cart));
    const before = mem.pairs || {};
    const isMain = (i) => i && !i.parent_relationship && !!R.products[i.handle];

    // 1. paid units per product (every variant, top-level lines only) and the anchor = its biggest line
    const units = {};
    const anchor = {};
    const mainVariants = new Set();
    for (const i of items) {
      if (!isMain(i)) continue;
      units[i.handle] = (units[i.handle] || 0) + i.quantity;
      mainVariants.add(i.variant_id);
      if (!anchor[i.handle] || i.quantity > anchor[i.handle].quantity) anchor[i.handle] = i;
    }
    const earned = {};
    for (const [h, u] of Object.entries(units)) for (const [g, n] of Object.entries(entitled(h, u))) if (n > 0) earned[`${h}>${g}`] = n;

    // 2. gift lines: under a known product (pairs) or loose
    const updates = {};
    const linesOf = {};
    const loose = {}; // gift handle -> [{line, left}]
    for (const i of items) {
      if (!R.gifts[i.handle]) continue;
      const parent = byKey.get(i.parent_relationship?.parent_key || '');
      if (isMain(parent)) {
        (linesOf[`${parent.handle}>${i.handle}`] = linesOf[`${parent.handle}>${i.handle}`] || []).push(i);
      } else if (parent && !R.complete) {
        continue; // under a product this page has no rules for: not ours to judge
      } else {
        // a $0 gift with no earning parent (cart link, old cart, a size swap done loose) never stays: owner 29/09
        // "a gift item on its own is charged at retail" - the paid real item is a different product; this $0 line goes
        (loose[i.handle] = loose[i.handle] || []).push({ line: i, left: i.quantity });
        updates[i.key] = 0; // re-hung below (same variant) when a product in the cart earns it
      }
    }

    // 3. real items that can turn back into gifts (#4): top-level lines of a mapped real variant
    const real = {}; // gift handle -> [{line, left}]
    const realNow = {};
    for (const i of items) {
      const c = R.claims[i.variant_id];
      if (!c || i.parent_relationship || isMain(i)) continue;
      (real[c.gift] = real[c.gift] || []).push({ line: i, left: i.quantity });
      realNow[c.gift] = (realNow[c.gift] || 0) + i.quantity;
    }
    // claimable = real units added since the last check (all of them when this cart was never checked)
    const claimable = {};
    for (const [g, n] of Object.entries(realNow)) claimable[g] = Math.max(0, n - (mem.fresh ? 0 : Number(mem.real?.[g]) || 0));
    const realAfter = { ...realNow };

    const adds = [];
    const paid = [];
    const next = {};
    const pairs = new Set([...Object.keys(earned), ...Object.keys(linesOf)]);
    for (const k of pairs) {
      const [main, gift] = k.split('>');
      const n = earned[k] || 0;
      const lines = linesOf[k] || [];
      const have = lines.reduce((s, l) => s + l.quantity, 0);
      const b = before[k];
      const holders = {};
      const held = {}; // gift variant -> qty kept
      // LOWER: keep the first lines, cut the rest
      let left = n;
      let got = 0;
      const cuts = [];
      for (const line of lines) {
        const keep = Math.min(line.quantity, left);
        left -= keep;
        if (keep !== line.quantity) {
          updates[line.key] = keep;
          cuts.push({ variant: line.variant_id, qty: line.quantity - keep });
        }
        if (keep > 0) {
          got += keep;
          const pv = byKey.get(line.parent_relationship.parent_key).variant_id;
          holders[pv] = (holders[pv] || 0) + keep;
          held[line.variant_id] = (held[line.variant_id] || 0) + keep;
        }
      }
      // #2: the part of the excess the CUSTOMER added to the gift line (it rose since our last check) becomes paid;
      // the rest (#5: the product earns less now) is only trimmed. No memory = never charge.
      if (cuts.length && b && !opt.noRaise) {
        let convert = Math.max(0, Math.min(have - n, have - (Number(b.h) || 0)));
        for (const c of cuts) {
          if (convert <= 0) break;
          const q = Math.min(c.qty, convert);
          convert -= q;
          const rv = R.realOf[c.variant];
          if (!rv) continue;
          paid.push({ id: rv, quantity: q, pair: k });
          realAfter[gift] = (realAfter[gift] || 0) + q;
        }
      }

      // ADD: (a) adopt loose units, (b) rise since the last check, (c) lost with the holder line, (d) #4 claim a real item
      let add = 0;
      const missing = n - got;
      const want = []; // gift variants to add
      if (missing > 0 && anchor[main]) {
        let need = missing;
        for (const o of loose[gift] || []) {
          if (need <= 0) break;
          const q = Math.min(o.left, need);
          if (q <= 0) continue;
          o.left -= q;
          need -= q;
          want.push([o.line.variant_id, q]);
        }
        const rise = n - (Number(b?.e) || 0);
        // owner 12/10/2026 ("quà tặng cho khách cứ add mới là có"): the paid units went up since the last check (a new add
        // from its page, + in the cart, an upsell) -> every gift it earns comes back, also one deleted earlier
        const grew = b?.u !== undefined && (units[main] || 0) > Number(b.u);
        let lost = 0;
        for (const [v, q] of Object.entries(b?.p || {})) if (!mainVariants.has(Number(v))) lost += Number(q) || 0;
        const auto = grew ? need : Math.min(need, Math.max(rise, Math.min(lost, (Number(b?.h) || 0) - got), 0));
        if (auto > 0) {
          need -= auto;
          // the sizes this pair held before (a belt in M stays M), then the default variant
          let rest = auto;
          for (const [v, q] of Object.entries(b?.g || {})) {
            const gone = Math.max(0, (Number(q) || 0) - (held[v] || 0));
            const t = Math.min(gone, rest);
            if (t > 0 && (R.gifts[gift].variants || []).includes(Number(v))) {
              want.push([Number(v), t]);
              rest -= t;
            }
          }
          if (rest > 0) {
            // owner 30/09 (combos): a newly earned gift of a product that already holds that gift takes the size it holds
            // (a 2nd belt in M when the 1st is M); none held → the default variant
            const same = Object.entries(held)
              .filter(([v, q]) => q > 0 && (R.gifts[gift].variants || []).includes(Number(v)))
              .sort((x, y) => y[1] - x[1])[0];
            want.push([same ? Number(same[0]) : R.gifts[gift].variant, rest]);
          }
        }
        let claim = Math.min(need, claimable[gift] || 0);
        for (const r of real[gift] || []) {
          if (claim <= 0) break;
          const q = Math.min(r.left, claim);
          if (q <= 0) continue;
          r.left -= q;
          claim -= q;
          need -= q;
          claimable[gift] -= q;
          realAfter[gift] -= q;
          updates[r.line.key] = r.left;
          want.push([R.claims[r.line.variant_id].variant, q]);
        }
        for (const [id, q] of want) {
          if (!id || q <= 0) continue;
          adds.push({ id: Number(id), quantity: q, parentVariant: anchor[main].variant_id, pair: k });
          holders[anchor[main].variant_id] = (holders[anchor[main].variant_id] || 0) + q;
          held[id] = (held[id] || 0) + q;
          add += q;
        }
      }
      if (n || got || add) next[k] = { e: n, h: got + add, p: holders, g: held, u: units[main] || 0 };
    }
    // merge adds of the same gift variant under the same holder
    const merged = [];
    for (const a of adds) {
      const m = merged.find((x) => x.id === a.id && x.parentVariant === a.parentVariant);
      if (m) m.quantity += a.quantity;
      else merged.push({ ...a });
    }
    return { updates, adds: merged, paid, next: { token: tokenOf(cart), pairs: next, real: realAfter }, cart };
  }
  const isEmpty = (p) => !Object.keys(p.updates).length && !p.adds.length && !p.paid.length;

  /* ---------------- cart I/O ---------------- */
  let lastCart = null;
  let lastCartAt = 0;
  let handedCart = null; // a cart the buy box / the fast path just received (saves one request)
  const keyVariant = new Map(); // every line key ever seen -> its variant (drawer rows keep old keys until the repaint)
  const remember = (cart) => {
    if (cart && cart.items) {
      if (!cart.token && lastCart?.token) cart.token = lastCart.token;
      lastCart = cart;
      lastCartAt = Date.now();
      for (const i of cart.items) keyVariant.set(i.key, i.variant_id);
    }
  };
  // sections/bh-product-cart.liquid: the whole cart inside a cart answer ({{ cart | json }} + line key -> parent key)
  const CART_SECTION = 'bh-product-cart';
  const readJSON = (html, attr) => {
    const m = new RegExp(`<script[^>]*${attr}>([\\s\\S]*?)<\\/script>`).exec(html || '');
    try {
      return m ? JSON.parse(m[1]) : null;
    } catch {
      return null;
    }
  };
  function cartFromAnswer(res) {
    const html = res?.sections?.[CART_SECTION];
    const cart = readJSON(html, 'data-bh-cart');
    if (!cart?.items) return null;
    const parents = readJSON(html, 'data-bh-cart-parents') || {};
    if (!cart.token && lastCart?.token) cart.token = lastCart.token;
    cart.items = cart.items.map((i) => (parents[i.key] ? { ...i, parent_relationship: { parent_key: parents[i.key] } } : i));
    return cart;
  }
  const drawerSections = (sections) => {
    if (!sections) return undefined;
    const out = { ...sections };
    delete out[CART_SECTION];
    return out;
  };
  const fetchCart = async () => {
    const cart = await (await fetch(jsonUrl(routes().cart_url), { headers: { Accept: 'application/json' } })).json();
    remember(cart);
    return cart;
  };
  const post = (url, body) =>
    fetch(jsonUrl(url), { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
  const postJson = async (url, body) => {
    const res = await post(url, body);
    const data = await res.json();
    if (!res.ok || data?.status) throw new Error(data?.description || data?.message || `${url} failed`);
    return data;
  };
  const sectionIds = () => [...document.querySelectorAll('cart-items-component')].map((c) => c.dataset.sectionId).filter(Boolean);

  /* ---------------- state + gate ---------------- */
  let stale = true; // lastCart may be behind the server (another script changed the cart and no check read it yet)
  const gate = { busy: false, inflight: 0, lastMutation: 0, lastQuantity: 0, owed: true };
  const pending = () => gate.owed || gate.busy || gate.inflight > 0 || root.classList.contains('bh-cart-busy') || Date.now() - gate.lastQuantity < 450;
  const paint = () => root.toggleAttribute('data-bh-gift-pending', pending());
  const own = new WeakSet();
  let noRaiseOnce = false;
  let failures = 0;
  let lastFailure = 0;
  let lastKey = '';
  let lastAt = 0;
  let repeats = 0;

  /** apply a plan: lower first (its answer has fresh line keys), then hang the adds on their product lines, repaint */
  async function apply(p) {
    const key = JSON.stringify([p.updates, p.adds, p.paid]);
    repeats = key === lastKey && Date.now() - lastAt < 5000 ? repeats + 1 : 0;
    lastKey = key;
    lastAt = Date.now();
    if (repeats >= 3) return; // the cart is not taking this fix: stop, never loop
    const ids = sectionIds();
    const sections = ids.length ? { sections: ids.join(','), sections_url: window.location.pathname } : {};
    const deferred = CartLinesUpdateEvent.createPromise();
    const ev = new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines: [], promise: deferred.promise });
    own.add(ev);
    document.dispatchEvent(ev);
    try {
      let data = p.cart;
      const adding = p.adds.length || p.paid.length;
      if (Object.keys(p.updates).length) data = await postJson(routes().cart_update_url, { updates: p.updates, ...(adding ? {} : sections) });
      if (adding) {
        const keyOf = (variant) => (data.items || []).find((i) => i.variant_id === variant && !i.parent_relationship)?.key;
        const items = [
          ...p.adds.map((a) => ({ id: a.id, quantity: a.quantity, parent_line_key: keyOf(a.parentVariant) })).filter((i) => i.parent_line_key),
          ...p.paid.map((a) => ({ id: a.id, quantity: a.quantity })),
        ];
        let done = null;
        if (items.length) {
          // the add answer brings back the drawer + the whole cart (bh-product-cart): no final update / read needed
          const withCart = { sections: [...ids, CART_SECTION].join(','), sections_url: window.location.pathname };
          const res = await post(routes().cart_add_url, { items, ...withCart });
          if (res.ok) {
            const ans = await res.json();
            const c = cartFromAnswer(ans);
            if (c) done = { ...c, sections: drawerSections(ans.sections) };
          } else if (items.length > 1) {
            // /cart/add.js is all-or-nothing: one unavailable item must not hold back the others
            for (const item of items) await post(routes().cart_add_url, { items: [item] }).catch(() => {});
          }
        }
        data = done || (await postJson(routes().cart_update_url, { updates: {}, ...sections }));
      }
      remember(data);
      writeMem(p.next);
      failures = 0;
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
        detail: { source: SOURCE, items: data.items, ...(data.sections ? { sections: drawerSections(data.sections) } : {}) },
      });
    } catch (error) {
      failures++;
      lastFailure = Date.now();
      deferred.reject(error);
      console.warn('[bh-gift-guard]', error);
    }
  }

  let running = null;
  let timer = 0;
  async function check(force = false) {
    if (!force && failures >= 3 && Date.now() - lastFailure < 20000) return; // pause after 3 failures, try again later
    gate.owed = false;
    const cart = handedCart && handedCart.cart.token && Date.now() - handedCart.at < 1500 ? handedCart.cart : await fetchCart();
    handedCart = null;
    remember(cart);
    stale = false;
    const noRaise = noRaiseOnce;
    noRaiseOnce = false;
    if (!cart.item_count) return writeMem({ token: tokenOf(cart), pairs: {}, real: {} });
    const p = plan(cart, { noRaise });
    if (isEmpty(p)) return writeMem(p.next);
    await apply(p);
  }
  async function runNow(force = false) {
    clearTimeout(timer);
    while (running) await running;
    gate.busy = true;
    paint();
    running = check(force).catch((e) => console.warn('[bh-gift-guard]', e));
    try {
      await running;
    } finally {
      running = null;
      gate.busy = false;
      paint();
    }
  }
  function schedule(ms = 250) {
    clearTimeout(timer);
    gate.owed = true;
    paint();
    timer = setTimeout(() => runNow(), ms);
  }
  /** resolves when no check is running or due and no cart request is on the wire */
  async function idle(max = 6000) {
    const until = Date.now() + max;
    while ((running || gate.inflight > 0) && Date.now() < until) await (running || sleep(50));
  }

  /* ---------------- buy-box helpers ---------------- */
  /**
   * The gifts a product page must send with `addUnits` more units of `handle`, so the cart ends at exactly what it earns:
   * entitled(units in cart + addUnits) − gifts already under that product. pick(giftHandle) = the size chosen on the page.
   */
  async function giftsForAdd(handle, addUnits, pick = () => 0) {
    await idle();
    const cart = lastCart && Date.now() - lastCartAt < 120000 ? lastCart : await fetchCart();
    readRules();
    const items = cart.items || [];
    const byKey = new Map(items.map((i) => [i.key, i]));
    let unitsNow = 0;
    for (const i of items) if (!i.parent_relationship && i.handle === handle) unitsNow += i.quantity;
    const held = {};
    for (const i of items) {
      const parent = byKey.get(i.parent_relationship?.parent_key || '');
      if (parent && parent.handle === handle && R.gifts[i.handle]) held[i.handle] = (held[i.handle] || 0) + i.quantity;
    }
    const out = [];
    for (const [g, n] of Object.entries(entitled(handle, unitsNow + addUnits))) {
      const q = n - (held[g] || 0);
      if (q > 0) out.push({ handle: g, id: Number(pick(g)) || R.gifts[g]?.variant, quantity: q });
    }
    return out.filter((x) => x.id);
  }
  /** the buy box added gifts itself: the next check must not read them as a customer raising a gift (#2) */
  const notePdpAdd = () => {
    noRaiseOnce = true;
  };
  const noteCart = (cart) => {
    remember(cart);
    handedCart = { cart, at: Date.now() };
  };

  /* ---------------- triggers ---------------- */
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    if (own.has(event)) return;
    stale = true;
    gate.owed = true;
    gate.lastMutation = Date.now();
    paint();
    const promise = event.promise ?? event.detail?.promise;
    const go = () => schedule(250);
    if (promise) promise.then(go, go);
    else go();
  });
  document.addEventListener('quantity-selector:update', () => {
    gate.lastQuantity = Date.now();
    paint();
    setTimeout(paint, 500);
  });
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    for (const el of document.querySelectorAll('[name="updates[]"]')) el.disabled = false;
    schedule(0);
  });

  // every cart mutation on the wire, whoever sent it (theme, apps, this file)
  const MUTATION = /\/cart\/(add|change|update|clear)(\.js)?$/;
  const nativeFetch = window.fetch;
  window.fetch = function (input, init) {
    const promise = nativeFetch.apply(this, arguments);
    try {
      const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input?.url || '';
      const method = String(init?.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();
      if (method !== 'GET' && MUTATION.test(new URL(url, location.href).pathname)) {
        gate.inflight++;
        gate.lastMutation = Date.now();
        paint();
        const done = () => {
          gate.inflight--;
          gate.lastMutation = Date.now();
          paint();
        };
        promise.then(done, done);
      }
    } catch {}
    return promise;
  };

  /* ---------------- checkout gate ---------------- */
  const style = document.createElement('style');
  style.textContent =
    'html[data-bh-gift-pending] .additional-checkout-buttons,html[data-bh-gift-pending] shopify-accelerated-checkout-cart{pointer-events:none;opacity:.55;transition:opacity .15s}';
  document.head.appendChild(style);
  let released = false;
  let holding = false;
  const neutralise = (form) => {
    for (const el of form.elements) if (el.name === 'updates[]') el.disabled = true;
  };
  // a quantity typed into a box and not sent yet (Check out pressed straight away): send it first through the cart API
  const pushTyped = async (form) => {
    const updates = {};
    for (const el of form.elements) {
      if (el.name !== 'updates[]' || el.disabled) continue;
      const key = el.closest('[data-key]')?.dataset.key;
      const now = el.value === '' ? NaN : Number(el.value);
      if (key && Number.isInteger(now) && now >= 0 && now !== Number(el.defaultValue)) updates[key] = now;
    }
    if (Object.keys(updates).length) await post(routes().cart_update_url, { updates });
  };
  window.addEventListener(
    'submit',
    async (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      const submitter = event.submitter;
      if (submitter ? submitter.name !== 'checkout' : form.id !== 'cart-form') return;
      if (released) {
        released = false;
        neutralise(form);
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      if (holding) return;
      holding = true;
      submitter?.setAttribute('aria-busy', 'true');
      const until = Date.now() + 6000;
      const quiet = () => gate.inflight === 0 && !running && Date.now() - gate.lastMutation > 350 && Date.now() - gate.lastQuantity > 450;
      const hold = async () => {
        await sleep(60);
        while (!quiet() && Date.now() < until) await sleep(50);
        await pushTyped(form);
        while (gate.inflight > 0 && Date.now() < until) await sleep(50);
        await runNow(true);
        while (gate.inflight > 0 && Date.now() < until) await sleep(50);
      };
      // never keep a buyer from checkout: a slow network lets the order through after 8 s (the Flow check is the net)
      await Promise.race([hold().catch(() => {}), sleep(8000)]);
      const liveForm = (form.isConnected ? form : document.getElementById(form.id)) || form;
      const button = [...document.querySelectorAll('[name="checkout"]')].find((b) => b.form === liveForm);
      neutralise(liveForm);
      released = true;
      try {
        if (button && liveForm.requestSubmit) liveForm.requestSubmit(button);
        else throw new Error('no requestSubmit');
      } catch {
        const flag = document.createElement('input');
        flag.type = 'hidden';
        flag.name = 'checkout';
        flag.value = '1';
        liveForm.appendChild(flag);
        liveForm.submit();
      }
      released = false;
      holding = false;
      submitter?.removeAttribute('aria-busy');
    },
    true
  );

  /* ---------------- fast + / − / remove in the drawer and on /cart ----------------
   * TW / SCENTREAT fast path (nested-gifts.md "Fast + / − with nested gifts", st-fast-cart-horizon4.js), on this planner:
   * the number changes at once, a ring spins on the clicked button, Check out rests, ONE request carries the product and
   * its gifts (+ → /cart/add.js: extra units + newly earned gifts with parent_id = that line's variant; − / remove →
   * /cart/update.js: the line and its gift lines together; if the line holding the gifts goes, the re-hung gifts follow in
   * a second request). Quick clicks queue; only the last answer repaints the drawer (AbortError for the steps).
   * + on a gift already at what its product earns = rule #2 in one request: a paid line of the real item, same size.
   * Anything else (other products, typed quantities, − on a gift) keeps the theme's path; the check above follows it. */
  const want = new Map(); // variant of a product line -> quantity the shopper wants
  const realWant = new Map(); // real variant -> extra paid units (#2 from the + of a gift)
  let pumping = null;
  const fastStyle = document.createElement('style');
  fastStyle.textContent =
    'cart-items-component .bh-busy{position:relative;color:transparent!important}cart-items-component .bh-busy svg{opacity:0}' +
    "cart-items-component .bh-busy::after{content:'';position:absolute;inset:0;margin:auto;width:14px;height:14px;border-radius:50%;border:2px solid rgb(0 0 0/.18);border-top-color:rgb(0 0 0/.7);animation:bh-g-spin .7s linear infinite}" +
    '@keyframes bh-g-spin{to{transform:rotate(360deg)}}cart-items-component .bh-removing{opacity:.4;transition:opacity .15s;pointer-events:none}' +
    'html.bh-cart-busy [name=checkout]{opacity:.6;pointer-events:none;transition:opacity .15s}';
  document.head.appendChild(fastStyle);
  const fastIdle = () => {
    if (pumping || want.size || realWant.size) return;
    root.classList.remove('bh-cart-busy');
    // only the guard's own spinners (assets/bh-cart.js marks and clears its own) — 29/09 cart polish
    for (const el of document.querySelectorAll('[data-bh-guard-spin]')) {
      el.classList.remove('bh-busy', 'bh-removing');
      el.removeAttribute('data-bh-guard-spin');
    }
    paint();
  };
  const spinOwn = (el, cls) => {
    el.classList.add(cls);
    el.setAttribute('data-bh-guard-spin', '');
  };
  const topLine = (cart, variant) => (cart?.items || []).find((i) => i.variant_id === variant && !i.parent_relationship);
  const unitsOf = (cart, handle) => (cart.items || []).reduce((s, i) => s + (!i.parent_relationship && i.handle === handle ? i.quantity : 0), 0);

  async function sendFast(cart, line, qty) {
    const ids = sectionIds();
    const secs = { sections: [...ids, CART_SECTION].join(','), sections_url: window.location.pathname };
    const sim = { ...cart, items: [] };
    for (const i of cart.items) {
      if (i.key === line.key) {
        if (qty > 0) sim.items.push({ ...i, quantity: qty });
      } else if (!(qty === 0 && i.parent_relationship?.parent_key === line.key)) sim.items.push(i);
    }
    const p = plan(sim, { noRaise: true });
    let res;
    let next;
    if (qty > line.quantity && !Object.keys(p.updates).length) {
      const items = [{ id: line.variant_id, quantity: qty - line.quantity }];
      for (const a of p.adds) {
        items.push({ id: a.id, quantity: a.quantity, parent_id: line.variant_id });
        const m = p.next.pairs[a.pair]?.p; // these gifts hang on THIS line, not on the planner's anchor
        if (m) {
          m[a.parentVariant] = (m[a.parentVariant] || 0) - a.quantity;
          if (m[a.parentVariant] <= 0) delete m[a.parentVariant];
          m[line.variant_id] = (m[line.variant_id] || 0) + a.quantity;
        }
      }
      res = await postJson(routes().cart_add_url, { items, ...secs });
      next = cartFromAnswer(res);
    } else {
      const updates = { ...p.updates, [line.key]: qty };
      if (qty === 0) for (const i of cart.items) if (i.parent_relationship?.parent_key === line.key) delete updates[i.key];
      res = await postJson(routes().cart_update_url, { updates, ...secs });
      next = res;
      if (p.adds.length) {
        // the line that held the gifts went: hang them on the line of that product that is left
        const keyOf = (v) => (res.items || []).find((i) => i.variant_id === v && !i.parent_relationship)?.key;
        const items = p.adds.map((a) => ({ id: a.id, quantity: a.quantity, parent_line_key: keyOf(a.parentVariant) })).filter((i) => i.parent_line_key);
        if (items.length) {
          res = await postJson(routes().cart_add_url, { items, ...secs });
          next = cartFromAnswer(res);
        }
      }
    }
    writeMem(p.next);
    return { cart: next, sections: res.sections };
  }

  const pump = () =>
    pumping ||
    (pumping = (async () => {
      try {
        while (want.size || realWant.size) {
          const ids = sectionIds();
          const secs = { sections: [...ids, CART_SECTION].join(','), sections_url: window.location.pathname };
          const cart = lastCart;
          let step;
          const deferred = CartLinesUpdateEvent.createPromise();
          const ev = new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines: [], promise: deferred.promise });
          own.add(ev);
          document.dispatchEvent(ev);
          try {
            if (want.size) {
              const [variant, qty] = want.entries().next().value;
              want.delete(variant);
              const line = topLine(cart, variant);
              if (!line || line.quantity === qty) {
                deferred.reject(new DOMException('Nothing to change', 'AbortError'));
                continue;
              }
              step = await sendFast(cart, line, qty);
            } else {
              const items = [...realWant.entries()].map(([id, quantity]) => ({ id, quantity }));
              realWant.clear();
              const res = await postJson(routes().cart_add_url, { items, ...secs });
              step = { cart: cartFromAnswer(res), sections: res.sections };
            }
            const full = step.cart?.items ? step.cart : await fetchCart();
            remember(full);
            stale = false;
            if (want.size || realWant.size) {
              deferred.reject(new DOMException('Superseded by a newer cart update', 'AbortError'));
              continue;
            }
            deferred.resolve({
              cart: CartLinesUpdateEvent.createCartFromAjaxResponse(full),
              detail: { items: full.items, source: SOURCE, sections: drawerSections(step.sections), didError: false },
            });
            noteCart(full);
            schedule(120); // safety net on the same cart, no network read
          } catch (error) {
            deferred.reject(error);
            throw error;
          }
        }
      } catch (error) {
        console.warn('[bh-gift-guard] fast path', error);
        stale = true;
        want.clear();
        realWant.clear();
        schedule(0);
      } finally {
        pumping = null;
        fastIdle();
      }
    })());

  window.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const button = target?.closest('cart-items-component button[name="plus"], cart-items-component button[name="minus"], cart-items-component .cart-items__remove');
      if (!button || button.disabled || !lastCart || running) return;
      // 29/09 cart polish: in the ~250 ms after an outside change (buy box add, assets/bh-cart.js) the guard waited for its check and
      // passed product lines to the plain path (+ a fix-up = 2 requests). When that change handed over its answer (noteCart) and
      // nothing is on the wire, that answer IS the server's cart: take the click now, one request (the check it owed is folded in).
      const handed = stale && !!handedCart && handedCart.cart === lastCart && !!handedCart.cart?.token && Date.now() - handedCart.at < 1500 && gate.inflight === 0 && !pumping;
      if (stale && !handed) return; // cart not known right now: the theme's path
      const adopt = () => {
        if (!stale) return;
        clearTimeout(timer); // the owed check is folded into this request; the pump schedules a safety check after it
        stale = false;
        handedCart = null;
        gate.owed = false;
      };
      const row = button.closest('[data-key]');
      const variant = keyVariant.get(row?.dataset.key || '');
      if (!variant) return;
      readRules();
      const cart = lastCart;
      const item = (cart.items || []).find((i) => i.key === row.dataset.key) || topLine(cart, variant);
      if (!item) return;
      const kind = button.getAttribute('name') === 'plus' ? 1 : button.getAttribute('name') === 'minus' ? -1 : 0;
      const input = row.querySelector('input[type="number"], input[name="updates[]"]');

      if (item.parent_relationship) {
        if (!R.gifts[item.handle] || kind <= 0) return; // − / remove on a gift: the theme's path (#3)
        const parent = cart.items.find((i) => i.key === item.parent_relationship.parent_key);
        if (!parent || !R.products[parent.handle]) return;
        const earned = entitled(parent.handle, unitsOf(cart, parent.handle))[item.handle] || 0;
        const have = cart.items.reduce((s, i) => s + (i.handle === item.handle && i.parent_relationship?.parent_key === parent.key ? i.quantity : 0), 0);
        if (have < earned) return; // still inside what it earns: the theme's path
        event.preventDefault();
        event.stopImmediatePropagation();
        const rv = R.realOf[item.variant_id];
        if (!rv) return; // no paid twin known: nothing to sell, the gift stays at what is earned
        adopt();
        realWant.set(rv, (realWant.get(rv) || 0) + 1);
        spinOwn(button, 'bh-busy');
        root.classList.add('bh-cart-busy');
        paint();
        pump();
        return;
      }
      if (!R.products[item.handle]) return; // other products: the theme's path

      event.preventDefault();
      event.stopImmediatePropagation();
      const current = want.has(variant) ? want.get(variant) : Number(input?.value || item.quantity) || 0;
      const qty = kind > 0 ? current + 1 : kind < 0 ? Math.max(0, current - 1) : 0;
      if (qty === current) return;
      adopt();
      want.set(variant, qty);
      if (input && qty > 0) input.value = String(qty);
      if (qty === 0) spinOwn(row, 'bh-removing');
      spinOwn(button, 'bh-busy');
      root.classList.add('bh-cart-busy');
      gate.lastMutation = Date.now();
      paint();
      pump();
    },
    true
  );

  BH.giftGuard = {
    rules: readRules,
    entitled: (handle, units) => (readRules(), entitled(handle, units)),
    plan,
    check: runNow,
    idle,
    giftsForAdd,
    notePdpAdd,
    noteCart,
    cartFromAnswer,
    cartSection: CART_SECTION,
    lastCart: () => lastCart,
  };

  // page load: a cart changed in another tab, an old cart, a cart link
  schedule(document.readyState === 'loading' ? 400 : 150);
}
